// Global Types
import type {
  $FullRegattaGroup,
  $FullUpsertRegattaEntry,
  $FullWebRegatta,
  $Model,
  $ModelUpsertParams,
} from 'common.data';
import type {
  $Language,
  $Translate,
} from 'common.language';

// Global Enums
import {
  countries,
  docRoute,
  docType,
  webPages,
  webSections,
} from 'common.enums';
import {
  GroupType,
  RegattaEntryCrewmemberGender,
  RegattaEntryCrewmemberTshirt,
  RegattaRegistrationStatus,
  RegattaRequirementType,
} from 'common.data';

// Global Components
import * as React from 'react';
import Formsy from 'formsy-react';
import {
  IoCloseSharp,
} from 'react-icons/io5';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import {
  CopyToClipboard,
} from 'react-copy-to-clipboard';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  cleanFormValues,
  getFileUrl,
  isDiff,
  numberValue,
  scrollTop,
} from '@vroskus/library-helpers/dist/browser';
import {
  i18next,
  regattaEntryCrewmemberGenderLabel,
  regattaEntryCrewmemberTshirtLabel,
} from 'common.language';
import {
  getDateTimeFormats,
  getDocumentUrl,
  getFullItemUrl,
  getRegionByRegionSeasonId,
} from 'common.helpers/dist/browser';
import config from '../../../../config';

// Services
import Services from '../../../../services';

// Components
import Loader from '../../../layout/Loader';
import Section from '../../../layout/Section';
import ConfirmDialog from '../../../dialog/ConfirmDialog';
import RegattaEntryCrewmembersDialog from '../../../dialog/RegattaEntryCrewmembersDialog';
import InputDate from '../../../form/InputDate';
import InputEmail from '../../../form/InputEmail';
import InputFile from '../../../form/InputFile';
import InputHidden from '../../../form/InputHidden';
import InputNo from '../../../form/InputNo';
import InputNumber from '../../../form/InputNumber';
import InputPhone from '../../../form/InputPhone';
import InputRegattaGroup from '../../../form/InputRegattaGroup';
import InputSelect from '../../../form/InputSelect';
import InputText from '../../../form/InputText';
import InputTypeahead from '../../../form/InputTypeahead';
import InputUniqueText from '../../../form/InputUniqueText';
import InputYesNo from '../../../form/InputYesNo';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  item: $Model<'Regatta'>;
  regattaEntryIdToEdit: null | string;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  activeRegattaGroup: $FullRegattaGroup | null;
  activeRegattaGroupCrewLimit: number;
  activeRegattaGroupCrewQuantity: number;
  data: $FullWebRegatta | null;
  error: Error | null;
  highlight: boolean;
  loading: boolean;
  regattaEntry: $FullUpsertRegattaEntry;
  showCrewmemberDetails: Array<number>;
  stage: 'form' | 'noform' | 'success' | null;
};

const groupTypePath = 'Group.Type';

const getOneRegattaEntryByRegistrationNo = async ({
  RegattaId,
  RegistrationNo,
}: {
  RegattaId: string;
  RegistrationNo: string;
}): Promise<$Model<'RegattaEntry'> | null> => {
  let output: $Model<'RegattaEntry'> | null = null;

  try {
    output = await Services.Api.getOneRegattaEntryByRegistrationNo({
      RegattaId,
      RegistrationNo,
    });
  } catch (error) {
    ErrorLog.exception(error as Error);
  }

  return output;
};

const getRegattaEntryRequirement = async ({
  RegattaRequirementId,
  Value,
}: {
  RegattaRequirementId: string;
  Value: string;
}): Promise<$Model<'RegattaEntryRequirement'> | null> => {
  let output: $Model<'RegattaEntryRequirement'> | null = null;

  try {
    output = await Services.Api.getRegattaEntryRequirementData({
      RegattaRequirementId,
      Value,
    });
  } catch (error) {
    ErrorLog.exception(error as Error);
  }

  return output;
};

class RegistrationSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = this.getInitialState();
  }

  getInitialState(): $State {
    const showCrewmemberDetails: Array<number> = this.shoudShowCrewmemberDetails() === true
      ? [0]
      : [];

    return {
      activeRegattaGroup: null,
      activeRegattaGroupCrewLimit: 0,
      activeRegattaGroupCrewQuantity: 0,
      data: null,
      error: null,
      highlight: false,
      loading: false,
      regattaEntry: this.getEmptyRegattaEntry(),
      showCrewmemberDetails,
      stage: null,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      regattaEntryIdToEdit,
    } = this.props;

    if (regattaEntryIdToEdit !== null && isDiff(
      prevProps.regattaEntryIdToEdit,
      regattaEntryIdToEdit,
    )) {
      scrollTop(webSections[webPages.regattaPage].registration);

      this.getRegattaEntryDataByAuth(regattaEntryIdToEdit);
    }
  }

  async onValidSubmit(values: Record<string, unknown>): Promise<void> {
    const {
      regattaEntry,
    } = this.state;

    try {
      await this.setStateAsync({
        error: null,
        loading: true,
      });

      const {
        RegattaEntryCrewmembers,
        RegattaEntryRequirements,
      } = values as {
        RegattaEntryCrewmembers: Array<$Model<'RegattaEntryCrewmember'>>;
        RegattaEntryRequirements?: Array<$Model<'RegattaEntryRequirement'>>;
      };

      const params = cleanFormValues<$FullUpsertRegattaEntry>({
        ...regattaEntry,
        ...values,
        PreliminaryCrewQuantity: values.PreliminaryCrewQuantityEnabled === true
          ? numberValue(values.PreliminaryCrewQuantityEnabled)
          : null,
        RegattaEntryCrewmembers: RegattaEntryCrewmembers.map((
          regattaEntryCrewmember: $Model<'RegattaEntryCrewmember'>,
          index: number,
        ) => ({
          ...cleanFormValues(regattaEntryCrewmember),
          Name: `${_.get(
            regattaEntryCrewmember,
            '_Firstname',
          )} ${_.get(
            regattaEntryCrewmember,
            '_Lastname',
          )}`,
          SortId: index + 1,
          Weight: regattaEntryCrewmember.Weight
            ? numberValue(regattaEntryCrewmember.Weight)
            : null,
        })),
        RegattaEntryRequirements: RegattaEntryRequirements
          ? RegattaEntryRequirements.map((
            regattaEntryRequirement: $Model<'RegattaEntryRequirement'>,
          ) => cleanFormValues(regattaEntryRequirement))
          : [],
      });

      await this.setStateAsync({
        regattaEntry: params,
      });

      const response = await Services.Api.upsertRegattaEntryData(params);

      await this.setStateAsync({
        loading: false,
        regattaEntry: {
          ...params,
          id: response.id,
          Uid: response.Uid,
        },
        stage: 'success',
      });
    } catch (error) {
      await this.setStateAsync({
        error: error as Error,
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }

    scrollTop(webSections[webPages.regattaPage].registration);
  }

  getEmptyRegattaEntry(): $FullUpsertRegattaEntry {
    const {
      item,
      main,
    } = this.props;

    const region = getRegionByRegionSeasonId({
      regions: main.regions,
      RegionSeasonId: item.RegionSeasonId,
    });

    return {
      BowNo: null,
      Club: null,
      CoachEmail: null,
      CoachName: null,
      CoachPhone: null,
      Content: null,
      EntryId: null,
      Harbor: null,
      Image: null,
      Lock: false,
      Model: null,
      Name: null,
      PreliminaryCrewQuantity: null,
      RegattaEntryCrewmembers: [{
        Birthdate: null,
        Email: null,
        Gender: null,
        Membership: null,
        Name: '',
        Phone: null,
        Qualification: null,
        RegattaEntryId: '',
        SortId: 1,
        Tshirt: null,
        Weight: null,
      }],
      RegattaEntryRequirements: [],
      RegattaGroupId: '',
      RegattaId: item.id,
      RegistrationNo: `${region.RegistrationLetters}-`,
      SailNo: null,
      TrackerId: null,
      Uid: 'new',
    };
  }

  setupSocket(): void {
    const {
      item,
    } = this.props;

    Services.Socket.on({
      action: (data) => {
        if (data.RegattaId === item.id && data.modelName === 'RegattaGroup') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    await this.getRegistrationData();
  }

  async getRegistrationData(): Promise<void> {
    const {
      item,
    } = this.props;
    const {
      regattaEntry,
    } = this.state;

    try {
      const params = Services.Navigation.getSearchParams();

      const Uid = _.get(
        params,
        'regatta_entry_uid',
      ) as string | undefined;

      await this.setStateAsync({
        error: null,
        loading: true,
      });

      const data = await Services.Api.getRegattaRegistrationSectionData({
        RegattaId: item.id,
      });

      if (data !== null) {
        const RegattaEntryRequirements = data.RegattaRequirements.map((
          regattaRequirement: $Model<'RegattaRequirement'>,
        ) => ({
          RegattaEntryId: '',
          RegattaRequirementId: regattaRequirement.id,
          Value: null,
        }));

        const stage = data.RegistrationStatus === RegattaRegistrationStatus.ongoing
          ? 'form'
          : 'noform';

        await this.setStateAsync({
          data,
          loading: false,
          regattaEntry: {
            ...regattaEntry,
            RegattaEntryRequirements,
          },
          stage,
        });
      }

      if (Uid) {
        scrollTop(webSections[webPages.regattaPage].registration);

        await this.getRegattaEntryDataByUid(Uid);
      }
    } catch (error) {
      await this.setStateAsync({
        error: error as Error,
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  async getRegattaEntryDataByUid(Uid: string): Promise<void> {
    try {
      await this.setStateAsync({
        error: null,
        loading: true,
      });

      const regattaEntry = await Services.Api.getOneRegattaEntryByUid({
        Uid,
      });

      if (regattaEntry !== null) {
        await this.setRegattaEntry(regattaEntry);

        await this.setStateAsync({
          loading: false,
        });
      } else {
        await this.setStateAsync({
          loading: false,
        });
      }
    } catch (error) {
      await this.setStateAsync({
        error: error as Error,
        loading: true,
      });

      ErrorLog.exception(error as Error);
    }
  }

  async getRegattaEntryDataByAuth(RegattaEntryId: string): Promise<void> {
    try {
      await this.setStateAsync({
        error: null,
        loading: true,
      });

      const regattaEntry = await Services.Api.getOneRegattaEntryByCrewmember({
        RegattaEntryId,
      });

      if (regattaEntry !== null) {
        await this.setRegattaEntry(regattaEntry);

        await this.setStateAsync({
          loading: false,
        });
      }
    } catch (error) {
      await this.setStateAsync({
        error: error as Error,
        loading: true,
      });

      ErrorLog.exception(error as Error);
    }
  }

  async setRegattaEntry(regattaEntry: $FullUpsertRegattaEntry): Promise<void> {
    const {
      data,
    } = this.state;
    let {
      showCrewmemberDetails,
    } = this.state;

    if (data !== null) {
      const foundRegattaGroup = _.find(
        data.RegattaGroups,
        {
          id: regattaEntry.RegattaGroupId,
        },
      ) as $FullRegattaGroup | undefined;

      await this.setActiveRegattaGroup(foundRegattaGroup || null);
    }

    if (this.shoudShowCrewmemberDetails() === true) {
      showCrewmemberDetails = [...Array(regattaEntry.RegattaEntryCrewmembers.length - 1).keys()];
    }

    const updatedRegattaEntry = regattaEntry;

    updatedRegattaEntry.RegattaEntryRequirements = _.sortBy(
      regattaEntry.RegattaEntryRequirements,
      'RegattaRequirementId',
    );

    await this.setStateAsync({
      regattaEntry: updatedRegattaEntry,
      showCrewmemberDetails,
    });
  }

  async setActiveRegattaGroup(activeRegattaGroup: $FullRegattaGroup | null): Promise<void> {
    let activeRegattaGroupCrewLimit = 0;
    let activeRegattaGroupCrewQuantity = 0;

    if (activeRegattaGroup !== null) {
      activeRegattaGroupCrewQuantity = activeRegattaGroup.Group.CrewQuantity || 0;
      activeRegattaGroupCrewLimit = activeRegattaGroup.CrewQuantityLimit || 0;

      if (activeRegattaGroup.PreliminaryCrewQuantityEnabled === true) {
        activeRegattaGroupCrewLimit = activeRegattaGroupCrewQuantity;
        activeRegattaGroupCrewQuantity = 0;
      }
    }

    await this.setStateAsync({
      activeRegattaGroup,
      activeRegattaGroupCrewLimit,
      activeRegattaGroupCrewQuantity,
    });
  }

  isRegattaGrouptFull(): boolean {
    const {
      activeRegattaGroup,
      data,
    } = this.state;

    if (data !== null && activeRegattaGroup !== null && activeRegattaGroup.EntriesQuantityLimit) {
      const regattaEntriesInActiveRegattaGroupQuantity: number = data.RegattaEntries.filter((
        item: $Model<'RegattaEntry'>,
      ) => item.RegattaGroupId === activeRegattaGroup.id).length;

      return regattaEntriesInActiveRegattaGroupQuantity >= activeRegattaGroup.EntriesQuantityLimit;
    }

    return false;
  }

  async fillRegattaEntryWithEntryData(value: null | string): Promise<void> {
    const {
      regattaEntry,
    } = this.state;

    const updatedRegattaEntry = regattaEntry;

    updatedRegattaEntry.EntryId = null;

    if (value) {
      try {
        const options = await Services.Api.findManyEntry({
          RegistrationNo: value,
        });

        if (options.length === 1) {
          const option: $Model<'Entry'> = options[0];

          updatedRegattaEntry.Club = option.Club;
          updatedRegattaEntry.EntryId = option.id;
          updatedRegattaEntry.Harbor = option.Harbor;
          updatedRegattaEntry.Image = option.Image;
          updatedRegattaEntry.Model = option.Model;
          updatedRegattaEntry.Name = option.Name;
        }
      } catch (error) {
        ErrorLog.exception(error as Error);
      }
    }

    await this.setStateAsync({
      regattaEntry: updatedRegattaEntry,
    });
  }

  shoudShowCrewmemberDetails(): boolean {
    const {
      item,
    } = this.props;
    const {
      RegistrationMembershipField,
      RegistrationTshirtField,
      RegistrationWeightField,
    } = item;

    return RegistrationMembershipField || RegistrationTshirtField || RegistrationWeightField;
  }

  shouldShowAddRegattaEntryCrewmemberButton(): boolean {
    const {
      activeRegattaGroupCrewLimit,
      activeRegattaGroupCrewQuantity,
      regattaEntry,
    } = this.state;

    if (activeRegattaGroupCrewQuantity !== 0) {
      return false;
    }

    if (regattaEntry !== null
      && activeRegattaGroupCrewLimit !== 0
      && regattaEntry.RegattaEntryCrewmembers.length >= activeRegattaGroupCrewLimit
    ) {
      return false;
    }

    return true;
  }

  async findRegattaEntryCrewmembers(
    search: string,
  ): Promise<Array<$Model<'RegattaEntryCrewmember'>>> {
    const {
      regattaEntry,
    } = this.state;

    let output: Array<$Model<'RegattaEntryCrewmember'>> = [];

    if (regattaEntry !== null && regattaEntry.EntryId) {
      try {
        output = await Services.Api.findManyRegattaEntryCrewmemberData({
          EntryId: regattaEntry.EntryId,
          search,
        });
      } catch (error) {
        ErrorLog.exception(error as Error);
      }
    }

    return output;
  }

  async deleteRegattaEntry(): Promise<void> {
    const {
      regattaEntry,
    } = this.state;

    try {
      if (regattaEntry !== null) {
        await this.setStateAsync({
          error: null,
          loading: true,
        });

        await Services.Api.deleteRegattaEntryData(regattaEntry);

        await this.setActiveRegattaGroup(null);

        await this.setStateAsync({
          loading: false,
          regattaEntry: this.getEmptyRegattaEntry(),
        });
      }
    } catch (error) {
      await this.setStateAsync({
        error: error as Error,
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  renderRegattaGroupPart() {
    const {
      t,
    } = this.props;
    const {
      data,
      highlight,
      regattaEntry,
    } = this.state;

    return (data !== null ? (
      <InputRegattaGroup
        className={'mb-3'}
        highlight={highlight}
        items={data.RegattaGroups}
        label={t('Regatta group')}
        name={'RegattaGroupId'}
        onChange={async (activeRegattaGroupId: string) => {
          const activeRegattaGroup: $FullRegattaGroup | null = _.find(
            data.RegattaGroups,
            {
              id: activeRegattaGroupId,
            },
          ) || null;

          await this.setActiveRegattaGroup(activeRegattaGroup);
        }}
        required
        value={regattaEntry.RegattaGroupId}
      />
    ) : null);
  }

  /* eslint-disable-next-line complexity */
  renderFormRegattaEntryPart() {
    const {
      t,
    } = this.props;
    const {
      activeRegattaGroup,
      data,
      highlight,
      regattaEntry,
    } = this.state;

    const isSportGroupType: boolean = _.eq(
      _.get(
        activeRegattaGroup,
        groupTypePath,
      ),
      GroupType.sport,
    );

    const debouncedSetEntry = _.debounce(
      (value: null | string) => {
        if (isSportGroupType === false) {
          this.fillRegattaEntryWithEntryData(value);
        }
      },
      1000,
    );

    return ((data !== null && activeRegattaGroup !== null && regattaEntry !== null) ? (
      <>
        <div
          className={'text-blue-900 font-semibold uppercase mb-3'}
          key={'regattaEntryPart'}
        >
          {t('Boat data')}
        </div>
        <div className={'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-3'}>
          {data.CountryEnabled === true ? (
            <>
              <InputSelect
                highlight={highlight}
                label={t('Country')}
                name={'RegistrationNo'}
                options={countries.map((country) => ({
                  id: `${country.code}-0`,
                  label: country.name,
                }))}
                required
                value={regattaEntry.RegistrationNo.at(-1) === '0' ? regattaEntry.RegistrationNo : null}
              />
              {data.SailNoEnabled && (
                <InputNo
                  highlight={highlight}
                  label={t('Sail no.')}
                  name={'SailNo'}
                  value={regattaEntry.SailNo}
                />
              )}
            </>
          ) : (
            <InputNo
              entryId={regattaEntry.EntryId}
              highlight={highlight}
              label={isSportGroupType ? t('Sail no.') : t('Registration no.')}
              name={'RegistrationNo'}
              onChange={debouncedSetEntry}
              required
              unique={async (RegistrationNo: string): Promise<boolean> => {
                if (data.PreventDuplicateRegistrationNo === true
                  && regattaEntry.RegistrationNo !== RegistrationNo
                ) {
                  const foundItem = await getOneRegattaEntryByRegistrationNo({
                    RegattaId: data.id,
                    RegistrationNo,
                  });

                  return foundItem === null;
                }

                return true;
              }}
              value={regattaEntry.RegistrationNo}
            />
          )}
          {isSportGroupType === false && (
            <>
              <InputText
                highlight={highlight}
                label={t('Boat name')}
                name={'Name'}
                required
                value={regattaEntry.Name}
              />
              <InputText
                highlight={highlight}
                label={t('Make / Model')}
                name={'Model'}
                value={regattaEntry.Model}
              />
              <InputFile
                highlight={highlight}
                image
                label={t('Image')}
                name={'Image'}
                value={regattaEntry.Image}
              />
            </>
          )}
          <InputText
            highlight={highlight}
            label={isSportGroupType ? t('City') : t('Harbor')}
            name={'Harbor'}
            required={data.RegistrationHarborField}
            value={regattaEntry.Harbor}
          />
          <InputText
            highlight={highlight}
            label={t('Club')}
            name={'Club'}
            required={data.RegistrationClubField}
            value={regattaEntry.Club}
          />
          {activeRegattaGroup.PreliminaryCrewQuantityEnabled === true && (
            <InputNumber
              highlight={highlight}
              label={t('Preliminary crew size')}
              name={'PreliminaryCrewQuantity'}
              value={regattaEntry.PreliminaryCrewQuantity}
            />
          )}
        </div>
      </>
    ) : null);
  }

  renderFormCoachPart() {
    const {
      t,
    } = this.props;
    const {
      activeRegattaGroup,
      highlight,
      regattaEntry,
    } = this.state;

    return ((regattaEntry !== null
      && activeRegattaGroup !== null
      && activeRegattaGroup.CoachEnabled === true
    ) ? (
      <div className={'sm:mb-3 -mx-5 mb-5 p-5 sm:m-0 sm:p-0 bg-orange-50 sm:bg-white'}>
        <div
          className={'text-blue-900 font-semibold uppercase mb-3'}
          key={'coachPart'}
        >
          {t('Coach')}
        </div>
        <div className={'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-3'}>
          <InputText
            highlight={highlight}
            label={t('Coach name')}
            name={'CoachName'}
            value={regattaEntry.CoachName}
          />
          <InputEmail
            highlight={highlight}
            label={t('Coach email')}
            name={'CoachEmail'}
            validations={'isEmail'}
            value={regattaEntry.CoachEmail}
          />
          <InputText
            highlight={highlight}
            label={t('Coach phone')}
            name={'CoachPhone'}
            value={regattaEntry.CoachPhone}
          />
        </div>
      </div>
      ) : null);
  }

  /* eslint-disable-next-line complexity */
  renderRegattaEntryCrewmember(
    regattaEntryCrewemeber: $ModelUpsertParams<'RegattaEntryCrewmember'>,
    index: number,
  ) {
    const {
      t,
    } = this.props;
    const {
      activeRegattaGroupCrewQuantity,
      data,
      highlight,
      regattaEntry,
      showCrewmemberDetails,
    } = this.state;

    return (
      <div key={`crewmeber${index}`}>
        {index === 0 && (
          <div
            className={'text-blue-900 font-semibold uppercase mb-3'}
            key={'skipper'}
          >
            {t('Skipper')}
          </div>
        )}
        {index === 1 && (
          <div
            className={'text-blue-900 font-semibold uppercase mb-3'}
            key={'crew'}
          >
            {t('Crew')}
          </div>
        )}
        <div className={`relative ${index === 0 ? 'sm:mb-3' : 'sm:mb-1'} -mx-5 mb-5 p-5 sm:m-0 sm:p-0 ${index % 2 === 0 ? 'bg-blue-50 sm:bg-white' : ''}`}>
          {index > 0 && (
            <div className={'sm:hidden mb-2'}>
              <span className={'font-bold'}>
                {index}
              </span>
              <span className={'uppercase'}>
                {` ${t('Crewmember')}`}
              </span>
            </div>
          )}
          <div className={'grid sm:grid-cols-6 gap-4'}>
            <div className={'sm:col-span-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1 mb-3 sm:mb-0'}>
              <div>
                {regattaEntryCrewemeber.id && (
                  <InputHidden
                    name={`RegattaEntryCrewmembers[${index}]id`}
                    value={regattaEntryCrewemeber.id}
                  />
                )}
                <InputHidden
                  name={`RegattaEntryCrewmembers[${index}]RegattaEntryId`}
                  value={regattaEntryCrewemeber.RegattaEntryId}
                />
                <InputHidden
                  name={`RegattaEntryCrewmembers[${index}]SortId`}
                  value={index + 1}
                />
                <InputTypeahead
                  getOptions={(search: string) => this.findRegattaEntryCrewmembers(search)}
                  highlight={highlight}
                  label={t('Firstname')}
                  labelClassName={index !== 0 ? 'lg:hidden' : ''}
                  name={`RegattaEntryCrewmembers[${index}]_Firstname`}
                  onOptionSelect={(option: $Model<'RegattaEntryCrewmember'>) => {
                    const appliedItem = {
                      ...regattaEntry.RegattaEntryCrewmembers[index],
                      Birthdate: option.Birthdate,
                      Email: option.Email,
                      Gender: option.Gender,
                      Membership: option.Membership,
                      Name: option.Name,
                      Phone: option.Phone,
                      Qualification: option.Qualification,
                      Tshirt: option.Tshirt,
                      Weight: option.Weight,
                    };

                    if (regattaEntry !== null) {
                      regattaEntry.RegattaEntryCrewmembers[index] = appliedItem;

                      this.setState({
                        regattaEntry,
                      });
                    }
                  }}
                  required
                  value={regattaEntryCrewemeber.Name.split(/ (.+)/)[0]}
                  valueFormatter={(v: string) => v.split(/ (.+)/)[0]}
                />
              </div>
              <InputText
                highlight={highlight}
                label={t('Lastname')}
                labelClassName={index !== 0 ? 'lg:hidden' : ''}
                name={`RegattaEntryCrewmembers[${index}]_Lastname`}
                required
                value={regattaEntryCrewemeber.Name.split(/ (.+)/)[1]}
              />
              <InputEmail
                highlight={highlight}
                label={t('Email')}
                labelClassName={index !== 0 ? 'lg:hidden' : ''}
                mask={typeof regattaEntry.id === 'undefined'}
                name={`RegattaEntryCrewmembers[${index}]Email`}
                required={index === 0 || _.get(
                  data,
                  'RegistrationEmailField',
                )}
                validations={'isEmail'}
                value={regattaEntryCrewemeber.Email || ''}
              />
              <InputPhone
                highlight={highlight}
                label={t('Phone')}
                labelClassName={index !== 0 ? 'lg:hidden' : ''}
                mask={typeof regattaEntry.id === 'undefined'}
                name={`RegattaEntryCrewmembers[${index}]Phone`}
                value={regattaEntryCrewemeber.Phone || ''}
              />
              <InputDate
                highlight={highlight}
                label={t('Birthdate')}
                labelClassName={index !== 0 ? 'lg:hidden' : ''}
                name={`RegattaEntryCrewmembers[${index}]Birthdate`}
                required={_.get(
                  data,
                  'RegistrationBirthdateField',
                )}
                value={regattaEntryCrewemeber.Birthdate}
              />
              <div className={'sm:hidden pt-7 w-full text-center'}>
                <button
                  className={'btn btn_light btn_outlined uppercase py-1'}
                  onClick={() => this.setState({
                    showCrewmemberDetails: _.xor(
                      showCrewmemberDetails,
                      [index],
                    ),
                  })}
                  type={'button'}
                >
                  {showCrewmemberDetails.includes(index) ? t('Less fields') : t('More fields')}
                </button>
              </div>
            </div>
            <div className={`sm:col-span-2 grid-cols-6 lg:grid-cols-12 gap-1 mb-3 sm:mb-0 ${showCrewmemberDetails.includes(index) ? 'grid' : 'hidden sm:grid'}`}>
              <InputNumber
                className={'col-span-2'}
                highlight={highlight}
                label={t('Weight')}
                labelClassName={index !== 0 ? 'lg:hidden' : ''}
                min={0}
                name={`RegattaEntryCrewmembers[${index}]Weight`}
                required={_.get(
                  data,
                  'RegistrationWeightField',
                )}
                value={regattaEntryCrewemeber.Weight}
              />
              <InputSelect
                className={'col-span-2'}
                highlight={highlight}
                label={t('Gender')}
                labelClassName={index !== 0 ? 'lg:hidden' : ''}
                name={`RegattaEntryCrewmembers[${index}]Gender`}
                options={_.map(
                  RegattaEntryCrewmemberGender,
                  (value) => ({
                    id: value,
                    label: regattaEntryCrewmemberGenderLabel[value],
                  }),
                )}
                value={regattaEntryCrewemeber.Gender}
              />
              <InputSelect
                className={'col-span-2'}
                highlight={highlight}
                label={t('Tshirt')}
                labelClassName={index !== 0 ? 'lg:hidden' : ''}
                name={`RegattaEntryCrewmembers[${index}]Tshirt`}
                options={_.map(
                  RegattaEntryCrewmemberTshirt,
                  (value) => ({
                    id: value,
                    label: regattaEntryCrewmemberTshirtLabel[value],
                  }),
                )}
                required={_.get(
                  data,
                  'RegistrationTshirtField',
                )}
                value={regattaEntryCrewemeber.Tshirt}
              />
              <InputText
                className={'col-span-3'}
                highlight={highlight}
                label={t('Qualif.')}
                labelClassName={index !== 0 ? 'lg:hidden' : ''}
                name={`RegattaEntryCrewmembers[${index}]Qualification`}
                value={regattaEntryCrewemeber.Qualification}
              />
              {data !== null && (
                <InputText
                  className={'col-span-3'}
                  highlight={highlight}
                  label={t('Member ID.')}
                  labelClassName={index !== 0 ? 'lg:hidden' : ''}
                  name={`RegattaEntryCrewmembers[${index}]Membership`}
                  required={_.get(
                    data,
                    'RegistrationMembershipField',
                  )}
                  value={regattaEntryCrewemeber.Membership}
                />
              )}
            </div>
          </div>
          {(activeRegattaGroupCrewQuantity === 0 && index !== 0) && (
            <button
              className={'absolute top-5 right-5 sm:-top-2 sm:-right-2 btn btn_danger btn_outlined p-0 top upprecase'}
              onClick={() => {
                if (regattaEntry !== null && regattaEntry.RegattaEntryCrewmembers) {
                  const updatedRegattaCrewmembers = [...regattaEntry.RegattaEntryCrewmembers];

                  updatedRegattaCrewmembers.splice(
                    index,
                    1,
                  );

                  regattaEntry.RegattaEntryCrewmembers = updatedRegattaCrewmembers;
                }

                this.setState({
                  regattaEntry,
                });
              }}
              type={'button'}
            >
              <IoCloseSharp className={'w-6 h-6 text-red-500'} />
            </button>
          )}
        </div>
      </div>
    );
  }

  renderRegattaEntryAddCrewmemberFromListButton() {
    const {
      t,
    } = this.props;
    const {
      regattaEntry,
      showCrewmemberDetails,
    } = this.state;

    return (regattaEntry.EntryId !== null ? (
      <RegattaEntryCrewmembersDialog
        entryId={regattaEntry.EntryId}
        onSubmit={(data: Array<$ModelUpsertParams<'RegattaEntryCrewmember'>>) => {
          const updatedShowCrewmemberDetails = showCrewmemberDetails;

          if (this.shoudShowCrewmemberDetails() === true) {
            data.forEach((dataItem, index: number) => {
              updatedShowCrewmemberDetails.push(index + showCrewmemberDetails.length);
            });
          }

          if (regattaEntry.RegattaEntryCrewmembers) {
            regattaEntry.RegattaEntryCrewmembers = [
              ...regattaEntry.RegattaEntryCrewmembers,
              ...data,
            ];
          }

          this.setState({
            regattaEntry,
            showCrewmemberDetails: updatedShowCrewmemberDetails,
          });
        }}
        regattaEntryId={regattaEntry.id || ''}
      >
        <button
          className={'btn btn_light btn_outlined uppercase mb-3'}
          type={'button'}
        >
          {t('Previous crewmembers')}
        </button>
      </RegattaEntryCrewmembersDialog>
    ) : null);
  }

  renderRegattaEntryAddCrewmemberButton() {
    const {
      t,
    } = this.props;
    const {
      activeRegattaGroupCrewLimit,
      regattaEntry,
      showCrewmemberDetails,
    } = this.state;

    const newRegattaEntryCrewmember = {
      Birthdate: null,
      Email: null,
      Gender: null,
      Membership: null,
      Name: '',
      Phone: null,
      Qualification: null,
      RegattaEntryId: '',
      SortId: 0,
      Tshirt: null,
      Weight: null,
    };

    return (this.shouldShowAddRegattaEntryCrewmemberButton() === true ? (
      <>
        <button
          className={'btn btn_light btn_outlined uppercase mb-3 mr-3'}
          onClick={() => {
            if (regattaEntry.RegattaEntryCrewmembers) {
              if (this.shoudShowCrewmemberDetails() === true) {
                showCrewmemberDetails.push(regattaEntry.RegattaEntryCrewmembers.length);
              }

              regattaEntry.RegattaEntryCrewmembers.push(newRegattaEntryCrewmember);
            }

            this.setState({
              regattaEntry,
              showCrewmemberDetails,
            });
          }}
          type={'button'}
        >
          {t('Add crewmember')}
          {activeRegattaGroupCrewLimit > 0 && ` (${t('Max')}: ${activeRegattaGroupCrewLimit})`}
        </button>
        {this.renderRegattaEntryAddCrewmemberFromListButton()}
      </>
    ) : null);
  }

  renderFormRegattaEntryCrewmembersPart() {
    const {
      activeRegattaGroupCrewQuantity,
      regattaEntry,
    } = this.state;

    if (regattaEntry !== null) {
      let regattaEntryCrewemebers: Array<$ModelUpsertParams<'RegattaEntryCrewmember'>> = [];

      if (activeRegattaGroupCrewQuantity > 0) {
        for (let index = 0; index < activeRegattaGroupCrewQuantity; index += 1) {
          if (regattaEntry.RegattaEntryCrewmembers.length >= index + 1) {
            regattaEntryCrewemebers.push(
              regattaEntry.RegattaEntryCrewmembers[index],
            );
          } else {
            regattaEntryCrewemebers.push({
              Birthdate: null,
              Email: null,
              Gender: null,
              Membership: null,
              Name: '',
              Phone: null,
              Qualification: null,
              RegattaEntryId: '',
              SortId: index + 1,
              Tshirt: null,
              Weight: null,
            });
          }
        }
      } else {
        regattaEntryCrewemebers = regattaEntry.RegattaEntryCrewmembers;
      }

      return (
        <>
          <div className={'mb-3'}>
            {regattaEntryCrewemebers.map((
              regattaEntryCrewemeber,
              index,
            ) => this.renderRegattaEntryCrewmember(
              regattaEntryCrewemeber,
              index,
            ))}
          </div>
          {this.renderRegattaEntryAddCrewmemberButton()}
        </>
      );
    }

    return null;
  }

  /* eslint-disable-next-line complexity */
  renderRegattaEntryRequirementValueInput({
    index,
    regattaEntryRequirement,
    regattaRequirement,
  }: {
    index: number;
    regattaEntryRequirement: $ModelUpsertParams<'RegattaEntryRequirement'>;
    regattaRequirement: $Model<'RegattaRequirement'>;
  }) {
    const {
      t,
    } = this.props;
    const {
      highlight,
    } = this.state;

    const valueInput: boolean = _.includes(
      [
        RegattaRequirementType.value,
      ],
      regattaRequirement.Type,
    );

    const valueUniqueInput: boolean = _.includes(
      [
        RegattaRequirementType.valueUnique,
      ],
      regattaRequirement.Type,
    );

    const fileInput: boolean = _.includes(
      [
        RegattaRequirementType.file,
        RegattaRequirementType.fileSource,
      ],
      regattaRequirement.Type,
    );

    const selectInput: boolean = _.includes(
      [
        RegattaRequirementType.select,
        // RegattaRequirementType.selectUnique,
      ],
      regattaRequirement.Type,
    );

    const booleanInput: boolean = _.includes(
      [
        RegattaRequirementType.boolean,
      ],
      regattaRequirement.Type,
    );

    return (
      <div>
        {valueInput && (
          <InputText
            highlight={highlight}
            name={`RegattaEntryRequirements[${index}]Value`}
            required={regattaRequirement.Required}
            value={regattaEntryRequirement.Value}
          />
        )}
        {valueUniqueInput && (
          <InputUniqueText
            highlight={highlight}
            name={`RegattaEntryRequirements[${index}]Value`}
            required={regattaRequirement.Required}
            unique={async (Value: string) => {
              if (Value !== null) {
                const foundItem = await getRegattaEntryRequirement({
                  RegattaRequirementId: regattaRequirement.id,
                  Value,
                });

                return foundItem === null;
              }

              return true;
            }}
            value={regattaEntryRequirement.Value}
          />
        )}
        {RegattaRequirementType.fileSource === regattaRequirement.Type
          && regattaRequirement.Source
          && (
            <a
              className={'btn btn_primary btn_outlined btn_block uppercase py-1 mb-3'}
              href={getFileUrl({
                cdnUrl: config.CDN_URL,
                file: regattaRequirement.Source,
              })}
              rel={'noreferrer'}
              target={'_blank'}
              title={t('Source file')}
            >
              <em className={'la la-file text-2xl'} />
              <span className={'ml-2'}>
                {t('Source file')}
              </span>
            </a>
          )}
        {fileInput && (
          <InputFile
            highlight={highlight}
            name={`RegattaEntryRequirements[${index}]Value`}
            required={regattaRequirement.Required}
            value={regattaEntryRequirement.Value}
          />
        )}
        {selectInput && (() => {
          let source: Array<{
            label: string;
            value: string;
          }> = [];

          if (regattaRequirement.Source) {
            try {
              source = JSON.parse(regattaRequirement.Source);
            } catch (error) {
              ErrorLog.exception(error as Error);
            }
          }

          const options = source.map((i) => ({
            id: JSON.stringify(i),
            label: i.label,
          }));

          return (
            <InputSelect
              className={'mb-0'}
              highlight={highlight}
              name={`RegattaEntryRequirements[${index}]Value`}
              options={options}
              required={regattaRequirement.Required}
              value={regattaEntryRequirement.Value}
            />
          );
        })()}
        {booleanInput && (
          <InputYesNo
            className={'mb-2'}
            highlight={highlight}
            labelFalse={t('No')}
            labelTrue={t('Yes')}
            name={`RegattaEntryRequirements[${index}]Value`}
            required={regattaRequirement.Required}
            value={regattaEntryRequirement.Value}
          />
        )}
      </div>
    );
  }

  renderRegattaEntryRequirement({
    index,
    regattaEntryRequirement,
    regattaRequirement,
  }: {
    index: number;
    regattaEntryRequirement: $ModelUpsertParams<'RegattaEntryRequirement'>;
    regattaRequirement: $Model<'RegattaRequirement'>;
  }) {
    const name: string | undefined = _.get(
      regattaRequirement,
      'Name',
    );

    const content: string | undefined = _.get(
      regattaRequirement,
      'Content',
    );

    return (
      <>
        <div className={'flex-grow'}>
          <div className={'col-span-2'}>
            <p className={'text-gray-600 mb-1 text-xs sm:text-sm tracking-wide uppercase'}>
              <span>{name}</span>
              {regattaRequirement.Required === true && (
                <span className={'ml-1 text-red-600'}>{'*'}</span>
              )}
            </p>
            <div className={'mb-3'}>
              <small>{content}</small>
            </div>
          </div>
          {regattaEntryRequirement.id && (
            <InputHidden
              name={`RegattaEntryRequirements[${index}]id`}
              value={regattaEntryRequirement.id}
            />
          )}
          <InputHidden
            name={`RegattaEntryRequirements[${index}]RegattaRequirementId`}
            value={regattaEntryRequirement.RegattaRequirementId}
          />
          <InputHidden
            name={`RegattaEntryRequirements[${index}]RegattaEntryId`}
            value={regattaEntryRequirement.RegattaEntryId}
          />
        </div>
        {this.renderRegattaEntryRequirementValueInput({
          index,
          regattaEntryRequirement,
          regattaRequirement,
        })}
      </>
    );
  }

  renderFormRegattaRequirementsPart() {
    const {
      t,
    } = this.props;
    const {
      data,
      regattaEntry,
    } = this.state;

    return (regattaEntry !== null
      && data !== null
      && regattaEntry.RegattaEntryRequirements
      && regattaEntry.RegattaEntryRequirements.length > 0 ? (
        <>
          <div
            className={'text-blue-900 font-semibold uppercase mb-3'}
            key={'regattaRequirementsPart'}
          >
            {t('Other data')}
          </div>
          <div className={'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'}>
            {regattaEntry.RegattaEntryRequirements.map((
              regattaEntryRequirement: $ModelUpsertParams<'RegattaEntryRequirement'>,
              index: number,
            ) => {
              const regattaRequirement: $Model<'RegattaRequirement'> | undefined = _.find(
                data.RegattaRequirements,
                {
                  id: regattaEntryRequirement.RegattaRequirementId,
                },
              );

              if (regattaRequirement) {
                return (
                  <div
                    className={'border rounded p-2 mb-3 flex flex-col'}
                    key={regattaEntryRequirement.id}
                  >
                    {this.renderRegattaEntryRequirement({
                      index,
                      regattaEntryRequirement,
                      regattaRequirement,
                    })}
                  </div>
                );
              }

              return null;
            })}
          </div>
        </>
      ) : null);
  }

  renderFormButtonsPart() {
    const {
      t,
    } = this.props;
    const {
      regattaEntry,
    } = this.state;

    return (
      <div className={'flex flex-row'}>
        {regattaEntry.Uid !== 'new' && (
        <ConfirmDialog
          content={t('Do you really want to resign from regatta?')}
          onConfirm={() => this.deleteRegattaEntry()}
          title={t('Confirmation needed')}
        >
          <button
            className={'btn btn_danger btn_outlined uppercase mr-3'}
            type={'button'}
          >
            {t('Delete entry')}
          </button>
        </ConfirmDialog>
        )}
        <button
          className={'btn btn_primary btn_outlined uppercase ml-auto'}
          onClick={() => this.setState({
            highlight: true,
          })}
          type={'submit'}
        >
          {regattaEntry.Uid !== 'new' ? t('Update data') : t('Submit data')}
        </button>
      </div>
    );
  }

  renderFormPart() {
    const {
      t,
    } = this.props;

    return this.isRegattaGrouptFull() === true
      ? (
        <div className={'text-gray-600 text-center p-3'}>
          {t('The group has reached the entries limit')}
        </div>
      )
      : (
        <>
          {this.renderFormRegattaEntryPart()}
          {this.renderFormRegattaEntryCrewmembersPart()}
          {this.renderFormCoachPart()}
          {this.renderFormRegattaRequirementsPart()}
          {this.renderFormButtonsPart()}
        </>
      );
  }

  renderFormStage() {
    const {
      t,
    } = this.props;
    const {
      activeRegattaGroup,
    } = this.state;

    return (
      <div key={'formStage'}>
        <Formsy onValidSubmit={(values) => this.onValidSubmit(values)}>
          {this.renderRegattaGroupPart()}
          {activeRegattaGroup !== null
            ? this.renderFormPart()
            : (
              <div className={'text-gray-600 text-center p-3'}>
                {t('Choose the group you want to participate in')}
              </div>
            )}
        </Formsy>
      </div>
    );
  }

  renderSuccessStage() {
    const {
      item,
      main,
      t,
    } = this.props;
    const {
      activeRegattaGroup,
      regattaEntry,
    } = this.state;

    const isSportGroupType: boolean = _.eq(
      _.get(
        activeRegattaGroup,
        groupTypePath,
      ),
      GroupType.sport,
    );

    if (regattaEntry !== null) {
      const basePath = getFullItemUrl({
        basePath: '/regatta',
        item,
        mainDomain: config.DOMAIN,
        regions: main.regions,
      });
      const url = `${basePath}?regatta_entry_uid=${regattaEntry.Uid}`;

      return (
        <div
          className={'flex flex-col text-center py-5'}
          key={'successStage'}
        >
          <div className={'flex items-center mx-auto mb-5'}>
            <em className={'la la-check-circle text-4xl mr-1 text-green-600'} />
            <span className={'text-lg'}>
              {t('Success!')}
            </span>
          </div>
          <div className={'flex flex-col'}>
            <span className={'text-gray-600'}>
              {`${t('A link to edit registration')}:`}
            </span>
            <a
              className={'mb-2'}
              href={url}
            >
              {url}
            </a>
            <CopyToClipboard text={url}>
              <button
                className={'btn btn_light btn_outlined mx-auto uppercase px-2 py-1 mb-5'}
                type={'button'}
              >
                {t('Copy link')}
              </button>
            </CopyToClipboard>
            {regattaEntry.id && (
              <a
                className={'btn btn_secondary btn_outlined mx-auto uppercase'}
                href={getDocumentUrl({
                  cdnUrl: config.CDN_URL,
                  docRoute: docRoute.regattaEntry,
                  docType: docType.pdf,
                  id: regattaEntry.id,
                  language: i18next.language as $Language,
                })}
                rel={'noreferrer'}
                target={'_blank'}
              >
                <em className={'la la-file-alt text-2xl mr-1 -my-3'} />
                <span>
                  {t('Filled entry form')}
                </span>
              </a>
            )}
            {isSportGroupType && (
            <div className={'mt-3'}>
              <button
                className={'btn btn_light btn_outlined mx-auto uppercase'}
                onClick={async () => {
                  const initialState = this.getInitialState();

                  await this.setStateAsync({
                    ...initialState,
                  });

                  const {
                    regattaEntry: initialRegattaEntry,
                  } = this.state;

                  await this.setStateAsync({
                    regattaEntry: {
                      ...initialRegattaEntry,
                      CoachEmail: regattaEntry.CoachEmail,
                      CoachName: regattaEntry.CoachName,
                      CoachPhone: regattaEntry.CoachPhone,
                    },
                  });
                  await this.getData();
                }}
                type={'button'}
              >
                {t('Add another entry')}
              </button>
            </div>
            )}
          </div>
        </div>
      );
    }

    return <div />;
  }

  renderNoFormStage() {
    const {
      main,
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    const formats = getDateTimeFormats({
      RegionId: main.RegionId,
      regions: main.regions,
    });

    const dateTimeFormat = `${formats.Date} ${formats.Time}`;

    let reason = {
      icon: '',
      message: t('Not ready yet'),
    };

    if (data !== null) {
      const reasons = {
        [RegattaRegistrationStatus.ended]: {
          icon: 'la-hourglass-end',
          message: t('Registration has ended'),
        },
        [RegattaRegistrationStatus.off]: {
          icon: 'la-power-off',
          message: t('Registration is disabled'),
        },
        [RegattaRegistrationStatus.ongoing]: {
          icon: '',
          message: '',
        },
        [RegattaRegistrationStatus.upcoming]: {
          icon: 'la-clock',
          message: `${t('Registration will start soon')}: ${moment(data.RegistrationStartDate).format(dateTimeFormat)}`,
        },
      };

      reason = reasons[data.RegistrationStatus];
    }

    return (
      <div
        className={'text-gray-600 flex items-center justify-center py-5'}
        key={'noFormStage'}
      >
        {reason.icon !== null && <em className={`la ${reason.icon} text-2xl mr-1`} />}
        <span>{reason.message}</span>
      </div>
    );
  }

  renderStage() {
    const {
      stage,
    } = this.state;

    if (stage === 'form') {
      return this.renderFormStage();
    }

    if (stage === 'success') {
      return this.renderSuccessStage();
    }

    return this.renderNoFormStage();
  }

  renderError() {
    const {
      t,
    } = this.props;
    const {
      error,
    } = this.state;

    return (error !== null ? (
      <div className={'flex flex-col text-center mb-5'}>
        <div className={'flex items-center justify-center mb-3'}>
          <em className={'la la-times-circle text-4xl mr-1 text-red-600'} />
          <span className={'text-lg'}>
            {t('Error!')}
          </span>
        </div>
        <div>
          {`${t('An error occured')} :(`}
        </div>
      </div>
    ) : null);
  }

  render() {
    const {
      t,
    } = this.props;
    const {
      loading,
    } = this.state;

    return (
      <Section
        id={webSections[webPages.regattaPage].registration}
        title={t('Registration')}
      >
        <Loader
          fade
          loading={loading === true}
        >
          <>
            {this.renderStage()}
            {this.renderError()}
          </>
        </Loader>
      </Section>
    );
  }
}

export default RegistrationSectionView;
